import {
    keepPreviousData,
    useMutation,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";
import {
    organizationTaskTemplateContract,
    OrganizationTaskTemplateCreate,
    OrganizationTaskTemplateUpdate,
} from "@common/contracts/organization-task-template.contract";
import { chunk, sum } from "@common/helpers/collections";
import {
    ORGANIZATION_TASK_TEMPLATE_BULK_MAX_IDS,
    ORGANIZATION_TASK_TEMPLATE_LIST_MAX_LIMIT,
    OrganizationTaskTemplateSortBy,
    OrganizationTaskTemplateSortDirection,
    OrganizationTaskTemplateStatusFilter,
} from "@common/schemas/organization-task-template.schema";
import { TaskType } from "@common/types/task";
import { TaskTemplateAssigneeRole } from "@common/types/taskTemplate";
import {
    OrganizationTaskTemplate,
    OrganizationTaskTemplateSchema,
} from "@common/types/organizationTaskTemplate";
import { useScope } from "@state/scopeContext.lib";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { fetchAllPages } from "./fetchAllPages";

const organizationTaskTemplateClient = getBackendAPIClient(
    organizationTaskTemplateContract,
);

export const organizationTaskTemplateQueryKeys = {
    all: () => ["organization-task-template"] as const,
    organization: (organizationId: string) =>
        [
            ...organizationTaskTemplateQueryKeys.all(),
            "organization",
            organizationId,
        ] as const,
    list: (
        organizationId: string,
        params: OrganizationTaskTemplateListParams,
    ) =>
        [
            ...organizationTaskTemplateQueryKeys.organization(organizationId),
            "list",
            params,
        ] as const,
};

interface OrganizationTaskTemplatesWithUsage {
    templates: OrganizationTaskTemplate[];
    serviceTemplateCounts: Record<string, number>;
}

interface OrganizationTaskTemplateListParams {
    status: OrganizationTaskTemplateStatusFilter;
    types?: TaskType[];
    groupIds?: string[];
    assigneeRoles?: TaskTemplateAssigneeRole[];
    createdByUserIds?: number[];
    sortBy?: OrganizationTaskTemplateSortBy;
    sortDirection?: OrganizationTaskTemplateSortDirection;
}

interface UseOrganizationTaskTemplatesOptions extends Omit<
    OrganizationTaskTemplateListParams,
    "status"
> {
    status?: OrganizationTaskTemplateStatusFilter;
    isEnabled?: boolean;
}

function useOrganizationTaskTemplatesQuery<TData>({
    status = OrganizationTaskTemplateStatusFilter.ACTIVE,
    types,
    groupIds,
    assigneeRoles,
    createdByUserIds,
    sortBy,
    sortDirection,
    isEnabled = true,
    select,
}: UseOrganizationTaskTemplatesOptions & {
    select: (data: OrganizationTaskTemplatesWithUsage) => TData;
}) {
    const { organizationId } = useScope();
    const listParams: OrganizationTaskTemplateListParams = {
        status,
        types,
        groupIds,
        assigneeRoles,
        createdByUserIds,
        sortBy,
        sortDirection,
    };

    return useQuery({
        select,
        queryKey: organizationId
            ? organizationTaskTemplateQueryKeys.list(organizationId, listParams)
            : [],
        placeholderData: keepPreviousData,
        queryFn: async (): Promise<OrganizationTaskTemplatesWithUsage> => {
            if (!organizationId) {
                return { templates: [], serviceTemplateCounts: {} };
            }

            const serviceTemplateCounts: Record<string, number> = {};
            const templates = await fetchAllPages({
                fetchPage: async (offset) => {
                    const response =
                        await organizationTaskTemplateClient.listOrganizationTaskTemplates(
                            {
                                params: { organizationId },
                                query: {
                                    offset,
                                    limit: ORGANIZATION_TASK_TEMPLATE_LIST_MAX_LIMIT,
                                    ...listParams,
                                },
                            },
                        );

                    if (response.status !== 200) {
                        throw new Error("Failed to fetch task templates");
                    }

                    Object.assign(
                        serviceTemplateCounts,
                        response.body.serviceTemplateCounts,
                    );
                    return {
                        items: response.body.taskTemplates.map((template) =>
                            OrganizationTaskTemplateSchema.parse(template),
                        ),
                        hasMore: response.body.hasMore,
                    };
                },
            });
            return { templates, serviceTemplateCounts };
        },
        enabled: !!organizationId && isEnabled,
    });
}

const selectTemplates = ({ templates }: OrganizationTaskTemplatesWithUsage) =>
    templates;

const selectServiceTemplateCounts = ({
    serviceTemplateCounts,
}: OrganizationTaskTemplatesWithUsage) => serviceTemplateCounts;

export function useOrganizationTaskTemplates(
    options: UseOrganizationTaskTemplatesOptions = {},
) {
    return useOrganizationTaskTemplatesQuery({
        ...options,
        select: selectTemplates,
    });
}

export function useOrganizationTaskTemplateServiceTemplateCounts(
    options: UseOrganizationTaskTemplatesOptions = {},
) {
    return useOrganizationTaskTemplatesQuery({
        ...options,
        select: selectServiceTemplateCounts,
    });
}

const useInvalidateOrganizationTaskTemplates = () => {
    const queryClient = useQueryClient();
    const { organizationId } = useScope();

    return () => {
        if (!organizationId) {
            return;
        }
        void queryClient.invalidateQueries({
            queryKey:
                organizationTaskTemplateQueryKeys.organization(organizationId),
        });
    };
};

export function useCreateOrganizationTaskTemplateMutation() {
    const { organizationId } = useScope();
    const invalidate = useInvalidateOrganizationTaskTemplates();

    return useMutation({
        mutationFn: async (body: OrganizationTaskTemplateCreate) => {
            if (!organizationId) {
                throw new Error("Missing organizationId");
            }
            const response =
                await organizationTaskTemplateClient.createOrganizationTaskTemplate(
                    { params: { organizationId }, body },
                );
            if (response.status !== 201) {
                throw new Error("Failed to create task template");
            }
            return OrganizationTaskTemplateSchema.parse(response.body);
        },
        onSuccess: invalidate,
    });
}

interface UpdateOrganizationTaskTemplateVariables {
    templateId: string;
    body: OrganizationTaskTemplateUpdate;
}

export function useUpdateOrganizationTaskTemplateMutation() {
    const { organizationId } = useScope();
    const invalidate = useInvalidateOrganizationTaskTemplates();

    return useMutation({
        mutationFn: async ({
            templateId,
            body,
        }: UpdateOrganizationTaskTemplateVariables) => {
            if (!organizationId) {
                throw new Error("Missing organizationId");
            }
            const response =
                await organizationTaskTemplateClient.updateOrganizationTaskTemplate(
                    { params: { organizationId, templateId }, body },
                );
            if (response.status !== 200) {
                throw new Error("Failed to update task template");
            }
            return OrganizationTaskTemplateSchema.parse(response.body);
        },
        onSuccess: invalidate,
    });
}

export function useArchiveOrganizationTaskTemplateMutation() {
    const { organizationId } = useScope();
    const invalidate = useInvalidateOrganizationTaskTemplates();

    return useMutation({
        mutationFn: async (templateId: string) => {
            if (!organizationId) {
                throw new Error("Missing organizationId");
            }
            const response =
                await organizationTaskTemplateClient.archiveOrganizationTaskTemplate(
                    { params: { organizationId, templateId } },
                );
            if (response.status !== 200) {
                throw new Error("Failed to archive task template");
            }
            return OrganizationTaskTemplateSchema.parse(response.body);
        },
        onSuccess: invalidate,
    });
}

export function useUnarchiveOrganizationTaskTemplateMutation() {
    const { organizationId } = useScope();
    const invalidate = useInvalidateOrganizationTaskTemplates();

    return useMutation({
        mutationFn: async (templateId: string) => {
            if (!organizationId) {
                throw new Error("Missing organizationId");
            }
            const response =
                await organizationTaskTemplateClient.unarchiveOrganizationTaskTemplate(
                    { params: { organizationId, templateId } },
                );
            if (response.status !== 200) {
                throw new Error("Failed to enable task template");
            }
            return OrganizationTaskTemplateSchema.parse(response.body);
        },
        onSuccess: invalidate,
    });
}

export function useDetachOrganizationTaskTemplateMutation() {
    const { organizationId } = useScope();
    const invalidate = useInvalidateOrganizationTaskTemplates();

    return useMutation({
        mutationFn: async (templateId: string) => {
            if (!organizationId) {
                throw new Error("Missing organizationId");
            }
            const response =
                await organizationTaskTemplateClient.detachOrganizationTaskTemplate(
                    { params: { organizationId, templateId } },
                );
            if (response.status !== 200) {
                throw new Error("Failed to detach task template");
            }
            return response.body;
        },
        onSuccess: invalidate,
    });
}

export function useDeleteOrganizationTaskTemplateMutation() {
    const { organizationId } = useScope();
    const invalidate = useInvalidateOrganizationTaskTemplates();

    return useMutation({
        mutationFn: async (templateId: string) => {
            if (!organizationId) {
                throw new Error("Missing organizationId");
            }
            const response =
                await organizationTaskTemplateClient.deleteOrganizationTaskTemplate(
                    { params: { organizationId, templateId } },
                );
            if (response.status !== 200) {
                throw new Error("Failed to delete task template");
            }
        },
        onSuccess: invalidate,
    });
}

export type BulkOrganizationTaskTemplateAction =
    | "archive"
    | "unarchive"
    | "detach"
    | "delete";

interface BulkOrganizationTaskTemplateResult {
    affectedCount: number;
    failedCount: number;
}

const BULK_ACTION_REQUEST = {
    archive:
        organizationTaskTemplateClient.bulkArchiveOrganizationTaskTemplates,
    unarchive:
        organizationTaskTemplateClient.bulkUnarchiveOrganizationTaskTemplates,
    detach: organizationTaskTemplateClient.bulkDetachOrganizationTaskTemplates,
    delete: organizationTaskTemplateClient.bulkDeleteOrganizationTaskTemplates,
} satisfies Record<
    BulkOrganizationTaskTemplateAction,
    (typeof organizationTaskTemplateClient)[
        | "bulkArchiveOrganizationTaskTemplates"
        | "bulkUnarchiveOrganizationTaskTemplates"
        | "bulkDetachOrganizationTaskTemplates"
        | "bulkDeleteOrganizationTaskTemplates"]
>;

function useBulkOrganizationTaskTemplateMutation(
    action: BulkOrganizationTaskTemplateAction,
) {
    const { organizationId } = useScope();
    const invalidate = useInvalidateOrganizationTaskTemplates();

    return useMutation({
        mutationFn: async (
            templateIds: string[],
        ): Promise<BulkOrganizationTaskTemplateResult> => {
            if (!organizationId) {
                throw new Error("Missing organizationId");
            }
            const requestChunk = async (chunkIds: string[]) => {
                try {
                    const response = await BULK_ACTION_REQUEST[action]({
                        params: { organizationId },
                        body: { templateIds: chunkIds },
                    });
                    return response.status === 200
                        ? {
                              affectedCount: response.body.affectedCount,
                              failedCount: 0,
                          }
                        : { affectedCount: 0, failedCount: chunkIds.length };
                } catch {
                    return { affectedCount: 0, failedCount: chunkIds.length };
                }
            };
            const chunkResults = await Promise.all(
                chunk(templateIds, ORGANIZATION_TASK_TEMPLATE_BULK_MAX_IDS).map(
                    requestChunk,
                ),
            );
            return {
                affectedCount: sum(
                    chunkResults.map((result) => result.affectedCount),
                ),
                failedCount: sum(
                    chunkResults.map((result) => result.failedCount),
                ),
            };
        },
        onSuccess: invalidate,
    });
}

export const useBulkArchiveOrganizationTaskTemplatesMutation = () =>
    useBulkOrganizationTaskTemplateMutation("archive");

export const useBulkUnarchiveOrganizationTaskTemplatesMutation = () =>
    useBulkOrganizationTaskTemplateMutation("unarchive");

export const useBulkDetachOrganizationTaskTemplatesMutation = () =>
    useBulkOrganizationTaskTemplateMutation("detach");

export const useBulkDeleteOrganizationTaskTemplatesMutation = () =>
    useBulkOrganizationTaskTemplateMutation("delete");
